<template>
  <b-card
    no-body
  >
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <label>Mostrar</label>
          <v-select
            v-model="perPage"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block ml-50 mr-1"
          />
          <!-- Crear lista exige el candado de usuario, no solo ser admin. -->
          <b-button
            v-if="userData() && userData().can_edit_price_lists"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            @click="priceListAddSidebarActive = true"
          >
            <span class="align-middle">Nueva Lista</span>
          </b-button>
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              v-model="searchQuery"
              class="d-inline-block mr-1"
              placeholder="Buscar..."
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <div class="mx-2 mb-2">
      <b-row>
        <!-- Pagination info -->
        <b-col
          v-if="priceListsMeta"
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">Mostrando del {{ priceListsMeta.from }} al {{ priceListsMeta.to }} de {{ priceListsMeta.total }} registros</span>

          <download-excel
            v-if="priceListsData"
            class="btn"
            :data="priceListsData"
            worksheet="Listas de Precios"
            :name="'listas_precios_' + getCurrentDate() + '.xls'"
          >
            <feather-icon icon="DownloadIcon" />
            Descargar Excel
          </download-excel>
        </b-col>
      </b-row>
    </div>

    <b-table
      class="position-relative"
      hover
      responsive
      small
      :busy="tableIsBusy"
      primary-key="id"
      show-empty
      empty-text="No se encontraron registros coincidientes"
      :items="priceListsData"
      :fields="priceListsFields"
      :per-page="perPage"
    >
      <template #table-busy>
        <div class="text-center text-dark my-2">
          <b-spinner class="align-middle" />
        </div>
      </template>

      <template #head(id)="data">
        <span
          class="text-dark pointer"
          @click="sortColumn(data.column)"
        >{{ data.label }}
        </span>
      </template>

      <template #head(name)="data">
        <span
          class="text-dark pointer"
          @click="sortColumn(data.column)"
        >{{ data.label }}
        </span>
      </template>

      <!-- Column: Id -->
      <template #cell(id)="data">
        <b-link
          class="font-weight-bold text-primary"
          @click="$emit('addPriceListTab', data.item.id)"
        >
          {{ data.item.id }}
        </b-link>
      </template>

      <!-- Column: Name -->
      <template #cell(name)="data">
        <b-link
          class="font-weight-bold text-primary"
          @click="$emit('addPriceListTab', data.item.id)"
        >
          {{ data.item.name }}
        </b-link>
      </template>

      <!-- Column: marcas de gobierno -->
      <template #cell(flags)="data">
        <b-badge
          v-if="data.item.is_master"
          variant="light-primary"
          class="mr-50"
          title="De esta lista sale el precio base de los estilos"
        >
          Maestra
        </b-badge>
        <b-badge
          v-if="data.item.is_default"
          variant="light-info"
          class="mr-50"
          title="Se asigna a todo cliente nuevo"
        >
          Por defecto
        </b-badge>
        <!-- Minimo de unidades por documento (PRECIO VOLUMEN). -->
        <b-badge
          v-if="data.item.min_units_first_order"
          variant="light-info"
          class="mr-50"
          :title="`Primera compra: mínimo ${data.item.min_units_first_order} unidades. Siguientes: mínimo ${data.item.min_units_repeat_order || data.item.min_units_first_order}.`"
        >
          Mín. {{ data.item.min_units_first_order }} / {{ data.item.min_units_repeat_order || data.item.min_units_first_order }} uds.
        </b-badge>
        <!--
          Candado para QUIEN MIRA: abierto solo si su usuario y la lista tienen
          la edicion habilitada (can_edit). Con editable solo, un vendedor veia
          abierta una lista que no puede editar.
        -->
        <feather-icon
          :icon="data.item.can_edit ? 'UnlockIcon' : 'LockIcon'"
          size="14"
          :class="data.item.can_edit ? 'text-warning' : 'text-muted'"
          :title="lockHint(data.item)"
        />
      </template>

      <!-- Column: variantes con precio propio -->
      <template #cell(customized_count)="data">
        <span class="font-weight-bold">{{ data.item.customized_count }}</span>
        <small class="text-muted"> / {{ data.item.product_variants_count }}</small>
      </template>

      <!-- Column: Active -->
      <template #cell(active)="data">
        <b-badge :variant="`light-${ data.item.active ? 'success' : 'danger' }`">
          {{ data.item.active ? 'Activa' : 'Inactiva' }}
        </b-badge>
      </template>

      <!-- Column: Product Variants Count -->
      <template #cell(product_variants_count)="data">
        <b-badge variant="light-secondary">
          {{ data.item.product_variants_count || 0 }} productos
        </b-badge>
      </template>

      <!-- Column: Customers Count -->
      <template #cell(customers_count)="data">
        <b-badge variant="light-secondary">
          {{ data.item.customers_count || 0 }} clientes
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <b-dropdown
          variant="link"
          no-caret
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </template>

          <b-dropdown-item @click="$emit('addPriceListTab', data.item.id)">
            <feather-icon
              icon="EyeIcon"
              size="16"
              class="align-middle mr-50"
            />
            <span class="align-middle">Ver detalles</span>
          </b-dropdown-item>

          <!-- Editar y eliminar: solo con los dos candados abiertos (can_edit). -->
          <b-dropdown-item
            v-if="data.item.can_edit"
            @click="editPriceList(data.item.id)"
          >
            <feather-icon
              icon="EditIcon"
              size="16"
              class="align-middle mr-50"
            />
            <span class="align-middle">Editar</span>
          </b-dropdown-item>

          <b-dropdown-divider v-if="data.item.can_edit && !data.item.is_master && !data.item.is_default" />

          <!-- La maestra y la por defecto no se pueden eliminar (el servidor lo rechaza). -->
          <b-dropdown-item
            v-if="data.item.can_edit && !data.item.is_master && !data.item.is_default"
            variant="danger"
            @click="deletePriceList(data.item.id, data.item.name)"
          >
            <feather-icon
              icon="TrashIcon"
              size="16"
              class="align-middle mr-50"
            />
            <span class="align-middle">Eliminar</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>

    </b-table>

    <div class="mx-2 mb-2">
      <b-row>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="12"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-if="priceListsMeta"
            v-model="currentPage"
            :total-rows="priceListsMeta.total"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>

    <!-- Price List Add Sidebar -->
    <price-list-add
      :price-list-add-sidebar-active.sync="priceListAddSidebarActive"
      @refreshData="getPriceLists"
      @addPriceListTab="$emit('addPriceListTab', $event)"
    />
  </b-card>
</template>

<script>
import axios from '@axios'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import downloadExcel from 'vue-json-excel'
import PriceListAdd from './PriceListAdd.vue'

export default {
  components: {
    vSelect,
    downloadExcel,
    PriceListAdd,
  },

  directives: {
    Ripple,
  },

  data() {
    return {
      searchQuery: '',
      sortField: 'id',
      sortDesc: 'desc',
      perPage: 10,
      perPageOptions: [5, 10, 25, 50, 100],
      currentPage: 1,
      priceListAddSidebarActive: false,
      searchQueryTimeout: null,

      priceListsFields: [
        { key: 'id', label: '#' },
        { key: 'name', label: 'Nombre' },
        { key: 'description', label: 'Descripción' },
        { key: 'flags', label: '' },
        { key: 'active', label: 'Estado' },
        { key: 'customized_count', label: 'Precios propios' },
        { key: 'customers_count', label: 'Clientes' },
        { key: 'created_at', label: 'Creado' },
        { key: 'actions', label: 'Acciones' },
      ],
      priceListsData: null,
      priceListsMeta: null,
      tableIsBusy: true,
    }
  },

  computed: {
    userData() {
      return () => JSON.parse(localStorage.getItem('DuttonAuthUser'))
    },
  },

  watch: {
    perPage() {
      this.tableIsBusy = true
      this.getPriceLists()
    },
    currentPage() {
      this.tableIsBusy = true
      this.getPriceLists()
    },
    searchQuery() {
      // Limpiar el timeout anterior
      clearTimeout(this.searchQueryTimeout)

      if (!this.searchQuery || this.searchQuery.trim() === '') {
        this.currentPage = 1
        this.tableIsBusy = true
        this.getPriceLists()
      } else {
        // si hay texto, esperar 300ms para no saturar el servidor
        this.searchQueryTimeout = setTimeout(() => {
          this.currentPage = 1
          this.tableIsBusy = true
          this.getPriceLists()
        }, 300)
      }
    },
  },

  created() {
    this.getPriceLists()
  },

  methods: {
    lockHint(list) {
      if (list.can_edit) return 'Puedes editar esta lista'
      if (!list.editable) return 'Lista cerrada a edición'
      return 'Solo lectura: tu usuario no tiene habilitada la edición de listas'
    },

    getCurrentDate() {
      const date = new Date()
      return `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}${date.getDate().toString().padStart(2, '0')}`
    },

    getPriceLists() {
      if (this.perPage === 'Todos') {
        this.perPage = this.priceListsMeta?.total || 100
      }

      axios
        .get(`price-list?perPage=${this.perPage}&page=${this.currentPage}&query=${this.searchQuery}&sortField=${this.sortField}&sortDesc=${this.sortDesc}`)
        .then(response => {
          this.tableIsBusy = false
          this.priceListsMeta = response.data.meta
          this.priceListsData = response.data.data
        })
        .catch(error => {
          this.showErrors(error)
        })
    },

    editPriceList(id) {
      this.$emit('editPriceListTab', id)
    },

    deletePriceList(id, name) {
      this.$bvModal
        .msgBoxConfirm('¿Deseas eliminar la lista de precios?', {
          title: 'Eliminar lista de precios',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'SI',
          cancelTitle: 'NO',
          okTitleVariant: 'outline-danger',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value) {
            axios
              .delete(`price-list/${id}`)
              .then(() => {
                this.makeToast('success', 'Lista eliminada', `Se ha eliminado la lista de precios "${name}".`)
                this.getPriceLists()
              })
              .catch(error => {
                this.showErrors(error)
              })
          }
        })
    },

    sortColumn(field) {
      if (this.sortField === field) {
        if (this.sortDesc === 'asc') {
          this.sortDesc = 'desc'
        } else {
          this.sortDesc = 'asc'
        }
      } else {
        this.sortField = field
        this.sortDesc = 'asc'
      }
      this.getPriceLists()
    },

    showErrors(error) {
      if (error.response && error.response.data && error.response.data.data) {
        const errors = error.response.data.data
        Object.keys(errors).forEach(field => {
          this.makeToast('danger', 'Error', `${field}: ${errors[field][0]}`)
        })
      } else {
        this.makeToast('danger', 'Error', 'Ha ocurrido un error inesperado.')
      }
    },

    makeToast(variant = null, title = null, message = null) {
      this.$bvToast.toast(message, {
        title,
        variant,
        solid: true,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
.pointer {
  cursor: pointer;
}

::v-deep .badge-light-primary {
  background-color: #838383 !important;
  color: white !important;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
